import React from 'react';
import { Mail, Phone, MapPin, Linkedin, Twitter, Globe } from 'lucide-react';
import logo from '../assets/logo/logo.jpg';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white py-16">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Main Footer Content */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="bg-white rounded-lg flex items-center justify-center p-3">
                  <span className="text-white font-bold">
                    <img src={logo} alt="GF" style={{ height: '80px' }} />
                  </span>
                </div>
              </div>
              <p className="text-slate-300 mb-4 max-w-md leading-relaxed">
                Revolutionizing pressure vessel drawings through intelligent automation. 
                Transform your engineering workflow from days to hours with precision and reliability.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-slate-400 hover:text-white transition-colors duration-200">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" height={"25px"}><path fill="#ffffff" d="M576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 440 146.7 540.8 258.2 568.5L258.2 398.2L205.4 398.2L205.4 320L258.2 320L258.2 286.3C258.2 199.2 297.6 158.8 383.2 158.8C399.4 158.8 427.4 162 438.9 165.2L438.9 236C432.9 235.4 422.4 235 409.3 235C367.3 235 351.1 250.9 351.1 292.2L351.1 320L434.7 320L420.3 398.2L351 398.2L351 574.1C477.8 558.8 576 450.9 576 320z"/></svg>
                </a>
                <a href="#" className="text-slate-400 hover:text-white transition-colors duration-200">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" height={"25px"}><path fill="#ffffff" d="M196.3 512L103.4 512L103.4 212.9L196.3 212.9L196.3 512zM149.8 172.1C120.1 172.1 96 147.5 96 117.8C96 103.5 101.7 89.9 111.8 79.8C121.9 69.7 135.6 64 149.8 64C164 64 177.7 69.7 187.8 79.8C197.9 89.9 203.6 103.6 203.6 117.8C203.6 147.5 179.5 172.1 149.8 172.1zM543.9 512L451.2 512L451.2 366.4C451.2 331.7 450.5 287.2 402.9 287.2C354.6 287.2 347.2 324.9 347.2 363.9L347.2 512L254.4 512L254.4 212.9L343.5 212.9L343.5 253.7L344.8 253.7C357.2 230.2 387.5 205.4 432.7 205.4C526.7 205.4 544 267.3 544 347.7L544 512L543.9 512z"/></svg>
                </a>
                <a href="#" className="text-slate-400 hover:text-white transition-colors duration-200">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" height={"25px"}><path fill="#ffffff" d="M453.2 112L523.8 112L369.6 288.2L551 528L409 528L297.7 382.6L170.5 528L99.8 528L264.7 339.5L90.8 112L236.4 112L336.9 244.9L453.2 112zM428.4 485.8L467.5 485.8L215.1 152L173.1 152L428.4 485.8z"/></svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => {
                      const element = document.getElementById('about');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-slate-300 hover:text-white transition-colors duration-200"
                  >
                    About GenieFab
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => {
                      const element = document.getElementById('benefits');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-slate-300 hover:text-white transition-colors duration-200"
                  >
                    Key Benefits
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => {
                      const element = document.getElementById('how-it-works');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-slate-300 hover:text-white transition-colors duration-200"
                  >
                    How It Works
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => {
                      const element = document.getElementById('pricing');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-slate-300 hover:text-white transition-colors duration-200"
                  >
                    Pricing
                  </button>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-semibold mb-3">Contact Us</h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-slate-300">Contact Email</div>
                    <a className="text-sm text-slate-400" href="mailto:support@geniefab.com">support@geniefab.com</a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-slate-300">Sales Email</div>
                    <a className="text-sm text-slate-400" href="mailto:sales@geniefab.com">sales@geniefab.com</a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-slate-300">Phone <span className="text-sm">(Sales)</span></div>
                    <div className="text-sm text-slate-400">+91 9898081785</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-slate-300">Location</div>
                    <div className="text-sm text-slate-400">India</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Features Highlight */}
          <div className="border-t border-slate-700 pt-8 mb-8">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold text-slate-200 mb-1">90%</div>
                <div className="text-sm text-slate-400">Time Reduction</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-200 mb-1">99%+</div>
                <div className="text-sm text-slate-400">Accuracy Rate</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-200 mb-1">Timely</div>
                <div className="text-sm text-slate-400">Support Available</div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-700 pt-8 flex flex-col md:flex-row items-center justify-end">
            <div className="flex space-x-6 text-sm text-slate-400">
            <div className="text-slate-400 text-sm mb-4 md:mb-0">
              © {currentYear} GenieFab. All rights reserved.
            </div>
              <a href="#" className="hover:text-white transition-colors duration-200">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors duration-200">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors duration-200">Support</a>
            </div>
          </div>

          {/* Launch Offer Reminder */}
          <div className="mt-8 bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-6 text-center">
            <div className="text-lg font-semibold mb-2">⚡ Limited Time Launch Offer!</div>
            <div className="text-slate-300 mb-4">Get 50% OFF on your GenieFab subscription - Only ₹ XX,XXX/year</div>
            <button 
              onClick={() => {
                const element = document.getElementById('pricing');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-white text-slate-800 hover:bg-slate-100 px-6 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Claim Offer Now
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;